/**
 * src/pages/Delivered.tsx
 *
 * Every deal that actually reached a user, as a feed: the photo, the name the
 * card used, price and saving, who received it and on which tier, and whether
 * the deal would still go out today. For keeping an eye on what beta and
 * community testers are being sent.
 *
 * One card per deal; the recipients are listed inside it. Filters persist in
 * the URL so a link to "free-tier phones this week" can be shared.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, ImageOff, ChevronDown, ChevronUp, Search, Flag } from 'lucide-react';
import {
  fetchDelivered, flagDelivered, DELIVERED_FLAG_REASONS,
  type DeliveredDeal, type DeliveredFilters, type DeliveredResult, type WhyAtSend,
} from '@/api/pipeline';
import ChartCard from '@/components/ChartCard';
import { cn } from '@/lib/cn';

const WINDOWS: Array<[number, string]> = [[6, '6h'], [24, '24h'], [72, '3d'], [168, '7d'], [720, '30d']];
const CATEGORIES = ['phones', 'laptops', 'consoles', 'audio', 'monitors', 'gpus', 'wearables'];
const SOURCES = ['cex_uk', 'ebay_uk', 'amazon_uk', 'argos_uk', 'box_uk', 'ebuyer_uk', 'musicmagpie_uk', 'hukd_uk'];
const TIERS = ['free', 'pro', 'reseller_pro'];

function readUrl(): DeliveredFilters {
  const p = new URLSearchParams(window.location.search);
  return {
    hours: Number(p.get('hours') ?? 24) || 24,
    category: p.get('category') ?? '', source: p.get('source') ?? '', tier: p.get('tier') ?? '',
    channel: p.get('channel') ?? '', status: p.get('status') ?? '', q: p.get('q') ?? '', tgId: p.get('tgId') ?? '',
  };
}
function writeUrl(f: DeliveredFilters) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== '' && v != null && !(k === 'hours' && v === 24)) p.set(k, String(v));
  const qs = p.toString();
  window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
}

const gbp = (n: number | null) => (n == null ? '—' : `£${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

function Chips({ title, data, active, onPick }: { title: string; data: Record<string, number>; active?: string; onPick: (k: string) => void }) {
  const rows = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      <span className="mr-1 text-slate-500">{title}</span>
      {rows.map(([k, n]) => (
        <button key={k} onClick={() => onPick(active === k ? '' : k)}
                className={cn('rounded border px-2 py-0.5', active === k
                  ? 'border-sky-600 bg-sky-900/40 text-sky-200'
                  : 'border-slate-800 bg-slate-950/60 text-slate-300 hover:bg-slate-800')}>
          {k} <b>{n}</b>
        </button>
      ))}
    </div>
  );
}

const pct = (p: number | null | undefined) => (p == null ? '—' : `${Math.round(p * 100)}%`);

/** "jev-latest@c2" → "Jev"; a backup model id → its short name. */
function verdictLabel(by: string | null): string {
  if (!by) return 'unknown';
  const model = by.split('@')[0];
  if (/^jev/i.test(model)) return 'Jev';
  if (/mbert/i.test(model)) return 'mbertX (old)';
  return `backup: ${model.split('/').pop()}`;
}

function WhyRow({ label, children, bad }: { label: string; children: React.ReactNode; bad?: boolean }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 shrink-0 text-slate-500">{label}</span>
      <span className={cn('min-w-0', bad ? 'text-amber-300' : 'text-slate-300')}>{children}</span>
    </div>
  );
}

/** Why the card went out, as the pipeline saw it at the moment it was sent. */
function WhyItWentOut({ w }: { w: WhyAtSend | null }) {
  if (!w) {
    return <div className="text-slate-500">Not recorded: sent before 25 Sept 2026, when send-time reasons started being kept.</div>;
  }
  const tv = w.textVerdict;
  const id = w.identity;
  const oldPipeline = (w.pipelineVersion ?? 0) < 2;
  return (
    <div className="space-y-0.5">
      <WhyRow label="pipeline" bad={oldPipeline}>{oldPipeline ? 'old pipeline (before Jev + catalogue)' : `v${w.pipelineVersion}`}</WhyRow>
      <WhyRow label="text" bad={!tv || !/^jev/i.test(tv.by ?? '')}>
        {tv
          ? <>{verdictLabel(tv.by)}: {tv.productType ?? '?'}, {tv.category ?? 'no category'}, {tv.isDevice === false ? 'not a device' : 'device'} · {pct(tv.confidence)}{!tv.used && ' · not used (stale or unsure)'}</>
          : 'no text verdict'}
        {w.categorySource && <span className="text-slate-500"> · category from {w.categorySource}</span>}
      </WhyRow>
      <WhyRow label="catalogue" bad={!id || id.reason !== 'resolved'}>
        {id
          ? <>{id.reason === 'resolved' ? (w.catalogueName ?? 'resolved') : id.reason} · p {pct(id.p)} · device {pct(id.isDevice)} · in list {pct(id.inList)} · one model {pct(id.unambiguous)}{id.cached ? ' · cached' : ''}{id.productCategory ? ` · product is ${id.productCategory}` : ''}</>
          : 'not asked'}
        {w.modelKeySource && w.modelKeySource !== 'catalogue' && <span className="text-amber-300"> · key from {w.modelKeySource}</span>}
      </WhyRow>
      <WhyRow label="photo" bad={!w.photo || w.photo.usable !== true}>
        {w.photo ? <>{w.photo.usable === true ? 'product' : w.photo.usable === false ? 'not the product' : 'not sure'} · {pct(w.photo.p)} · via {w.photo.via}</> : 'no photo check recorded'}
      </WhyRow>
    </div>
  );
}

function FlagForm({ d }: { d: DeliveredDeal }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>(DELIVERED_FLAG_REASONS[0][0]);
  const [note, setNote] = useState('');
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => flagDelivered(d.dealId, { reason, note: note.trim() || undefined }),
    onSuccess: () => { setOpen(false); setNote(''); qc.invalidateQueries({ queryKey: ['delivered'] }); },
  });
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-rose-300 hover:text-rose-200" title="Record this card as wrong. Labelled data only: the deal is not changed.">
        <Flag className="h-3 w-3" /> flag
      </button>
    );
  }
  return (
    <div className="mt-1 w-full space-y-1 rounded border border-rose-900/50 bg-rose-950/20 p-2">
      <select value={reason} onChange={e => setReason(e.target.value)} className="w-full rounded border border-slate-800 bg-slate-950 px-1 py-1 text-[11px] text-slate-200">
        {DELIVERED_FLAG_REASONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      <input value={note} onChange={e => setNote(e.target.value.slice(0, 500))} placeholder="note (optional)"
             className="w-full rounded border border-slate-800 bg-slate-950 px-1 py-1 text-[11px] text-slate-200" />
      <div className="flex items-center gap-2">
        <button onClick={() => m.mutate()} disabled={m.isPending} className="rounded bg-rose-700 px-2 py-0.5 text-[11px] text-white hover:bg-rose-600 disabled:bg-slate-800">
          {m.isPending ? 'saving…' : 'save flag'}
        </button>
        <button onClick={() => setOpen(false)} className="text-[11px] text-slate-400 hover:text-white">cancel</button>
        {m.isError && <span className="text-[11px] text-rose-300">failed: {(m.error as Error)?.message}</span>}
      </div>
    </div>
  );
}

function DealCard({ d }: { d: DeliveredDeal }) {
  const [why, setWhy] = useState(false);
  const [open, setOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const noPhotoSends = d.recipients.filter(r => r.photoSent === false).length;
  return (
    <div className={cn('flex flex-col overflow-hidden rounded border bg-slate-900/40',
      d.stillServable ? 'border-slate-800' : 'border-amber-800/70')}>
      <div className="relative flex h-44 items-center justify-center bg-white">
        {d.image && !imgFailed
          ? <img src={d.image} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setImgFailed(true)}
                 className="max-h-44 max-w-full object-contain" />
          : <div className="flex flex-col items-center text-xs text-slate-500"><ImageOff className="mb-1 h-5 w-5" />no image</div>}
        {noPhotoSends > 0 && (
          <span className="absolute left-1 top-1 rounded bg-slate-900/85 px-1.5 py-0.5 text-[10px] text-amber-300"
                title="The card went out as text for these sends: the photo was refused or could not be fetched.">
            sent without photo ×{noPhotoSends}
          </span>
        )}
        {d.flags.length > 0 && (
          <span className="absolute bottom-1 left-1 rounded bg-rose-900/90 px-1.5 py-0.5 text-[10px] text-rose-100"
                title={d.flags.map(f => `${f.reason}${f.note ? `: ${f.note}` : ''}`).join('\n')}>
            flagged: {[...new Set(d.flags.map(f => f.reason))].join(', ')}
          </span>
        )}
        {!d.stillServable && (
          <span className="absolute right-1 top-1 rounded bg-amber-900/90 px-1.5 py-0.5 text-[10px] text-amber-100"
                title={d.scoredByNewPipeline ? `decision now: ${d.decisionNow ?? 'unknown'}` : 'scored before the new pipeline'}>
            {d.scoredByNewPipeline ? `now ${d.decisionNow ?? '?'}` : 'old pipeline'}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-2 text-xs">
        <div className="font-medium leading-snug text-slate-100">{d.name}</div>
        {d.title && d.title !== d.name && <div className="line-clamp-2 text-[11px] text-slate-500" title={d.title}>{d.title}</div>}
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-base font-semibold text-emerald-300">{gbp(d.price)}</span>
          {d.anchorPrice != null && <span className="text-slate-500 line-through">{gbp(d.anchorPrice)}</span>}
          {d.discountPct != null && <span className="text-emerald-400">-{d.discountPct}%</span>}
          {d.dcsNow != null && <span className="ml-auto rounded bg-slate-800 px-1.5 text-slate-200" title="DCS now">DCS {d.dcsNow}</span>}
        </div>
        <div className="flex flex-wrap gap-1 text-[10px] text-slate-400">
          {d.category && <span className="rounded bg-slate-800 px-1">{d.category}</span>}
          {d.source && <span className="rounded bg-slate-800 px-1">{d.source}</span>}
          {d.identityReason && <span className={cn('rounded px-1', d.identityReason === 'resolved' ? 'bg-emerald-950 text-emerald-300' : 'bg-slate-800')}
                                     title="catalogue decision">{d.identityReason === 'resolved' ? 'catalogue ✓' : d.identityReason}</span>}
          {d.modelKey && <span className="truncate font-mono text-slate-600" title={d.modelKey}>{d.modelKey}</span>}
        </div>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pt-1 text-[11px] text-slate-400">
          <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 hover:text-slate-200">
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {d.sends} send{d.sends === 1 ? '' : 's'} · {ago(d.lastSent)}
          </button>
          <button onClick={() => setWhy(!why)} className="hover:text-slate-200">why it went out</button>
          <FlagForm d={d} />
          {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-200">listing <ExternalLink className="h-3 w-3" /></a>}
        </div>
        {why && <div className="rounded border border-slate-800 bg-slate-950/60 p-1.5 text-[11px]"><WhyItWentOut w={d.whyAtSend} /></div>}
        {open && (
          <div className="mt-1 max-h-48 overflow-y-auto rounded border border-slate-800">
            <table className="w-full text-[11px]">
              <tbody className="text-slate-300">
                {d.recipients.map((r, i) => (
                  <tr key={i} className="border-b border-slate-900">
                    <td className="px-1 py-0.5">{r.name ?? r.tgId ?? '—'}</td>
                    <td className="px-1 py-0.5 text-slate-500">{r.tier ?? '—'}</td>
                    <td className="px-1 py-0.5 text-slate-500">{r.channel}{r.status !== 'sent' && <span className="text-red-400"> {r.status}</span>}</td>
                    <td className="px-1 py-0.5">{r.photoSent === false ? <span className="text-amber-400">text</span> : r.photoSent ? 'photo' : '—'}</td>
                    <td className="px-1 py-0.5 text-right text-slate-500">{new Date(r.sentAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Delivered() {
  const [f, setF] = useState<DeliveredFilters>(readUrl);
  const [text, setText] = useState(f.q ?? '');
  const set = (patch: Partial<DeliveredFilters>) => { const next = { ...f, ...patch }; setF(next); writeUrl(next); };
  const q = useQuery<DeliveredResult>({ queryKey: ['delivered', f], queryFn: () => fetchDelivered(f), refetchInterval: 60_000 });
  const r = q.data;
  const [onlyProblems, setOnlyProblems] = useState(false);
  const deals = useMemo(() => (r?.deals ?? []).filter(d => !onlyProblems || !d.stillServable || d.flags.length > 0 || d.recipients.some(x => x.photoSent === false || x.status !== 'sent')), [r, onlyProblems]);
  const sel = 'rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs text-slate-200';

  return (
    <div className="space-y-4 p-4">
      <header>
        <h1 className="text-lg font-semibold text-slate-100">Delivered</h1>
        <p className="text-xs text-slate-500">
          Every deal that actually reached a user, as it went out. Amber border: the deal would not go out today
          (dropped since, or scored before the new pipeline).
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {WINDOWS.map(([h, label]) => (
            <button key={h} onClick={() => set({ hours: h })}
                    className={cn('rounded px-2 py-1 text-xs', f.hours === h ? 'bg-slate-700 text-slate-100' : 'bg-slate-900 text-slate-400 hover:bg-slate-800')}>{label}</button>
          ))}
        </div>
        <select className={sel} value={f.category} onChange={e => set({ category: e.target.value })}>
          <option value="">All categories</option>{CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className={sel} value={f.source} onChange={e => set({ source: e.target.value })}>
          <option value="">All sources</option>{SOURCES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className={sel} value={f.tier} onChange={e => set({ tier: e.target.value })}>
          <option value="">All tiers</option>{TIERS.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className={sel} value={f.channel} onChange={e => set({ channel: e.target.value })}>
          <option value="">All channels</option><option value="push">push</option><option value="pull">pull</option>
        </select>
        <select className={sel} value={f.status} onChange={e => set({ status: e.target.value })}>
          <option value="">Any status</option><option value="sent">sent</option><option value="failed">failed</option>
        </select>
        <form onSubmit={e => { e.preventDefault(); set({ q: text.trim() }); }} className="relative">
          <Search className="pointer-events-none absolute left-2 top-1.5 h-3.5 w-3.5 text-slate-500" />
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Search titles"
                 className="w-52 rounded border border-slate-800 bg-slate-950 py-1 pl-7 pr-2 text-xs text-slate-100" />
        </form>
        <input value={f.tgId ?? ''} onChange={e => set({ tgId: e.target.value.replace(/\D/g, '') })} placeholder="Telegram user id"
               className="w-36 rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs text-slate-100" />
        <label className="inline-flex items-center gap-1 text-xs text-slate-400">
          <input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} />Only problems
        </label>
      </div>

      <ChartCard title="Summary" isLoading={q.isLoading} isError={q.isError}>
        {r && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-4 text-sm text-slate-300">
              <span><b className="text-slate-100">{r.totals.sends}</b> sends</span>
              <span><b className="text-slate-100">{r.totals.deals}</b> deals</span>
              <span><b className="text-slate-100">{r.totals.users}</b> users</span>
              <span><b className="text-slate-100">{r.totals.withPhoto}</b> with photo · <b className={r.totals.withoutPhoto ? 'text-amber-300' : 'text-slate-100'}>{r.totals.withoutPhoto}</b> text only
                {r.totals.unknownPhoto > 0 && <span className="text-slate-500"> · {r.totals.unknownPhoto} not recorded (sent before 25 Sept 14:00 UTC)</span>}</span>
              {r.totals.failed > 0 && <span className="text-red-400">{r.totals.failed} failed</span>}
              {r.truncated && <span className="text-amber-400">window too large; showing the latest 5,000 sends</span>}
            </div>
            <Chips title="Category" data={r.byCategory} active={f.category} onPick={k => set({ category: k })} />
            <Chips title="Source" data={r.bySource} active={f.source} onPick={k => set({ source: k })} />
            <Chips title="Tier" data={r.byTier} active={f.tier} onPick={k => set({ tier: k })} />
          </div>
        )}
      </ChartCard>

      {r && deals.length === 0 && <p className="text-sm text-slate-500">Nothing delivered matches these filters.</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {deals.map(d => <DealCard key={d.dealId} d={d} />)}
      </div>
    </div>
  );
}
